/* Common Imports */

import { styled, useTheme } from "@mui/system";
import React from "react";

/* Redux & Type Imports */

import { ObjectId } from "bson";
import { NextRouter } from "next/router";
import StateInterface from "../../../../redux-magic/state-interface";

/* Component Imports */

import { DeleteOutline, SendOutlined } from "@mui/icons-material";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import {
	CircularProgress,
	Drawer,
	Fade,
	FormControl,
	IconButton,
	RadioGroup,
	Snackbar,
	TextField,
} from "@mui/material";
import validateFiles from "../../../../lib/validateFiles";
import { sendChatMessageThunk } from "../../../../redux-magic/store";
import BeegruButton from "../../../common-components/buttons/BeegruButton";

/* Styled Components */

const RootDiv = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "100%",
	gap: "0.5rem",
	background: theme.palette.background.default,
	padding: "0rem 0.75rem 1rem 0.75rem",
}));

const ChatContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "flex-start",
	width: "100%",
	height: "100%",
	padding: "0.75rem 1rem 0.75rem 1rem",
	gap: "0.5rem",
	borderRadius: "2rem",
	boxShadow: "0px 4px 18px 0px rgba(0, 0, 0, 0.05)",
	background: theme.palette.background.paper,
	transition: "width 0.5s ease-in-out", // Add smooth width transition
}));

const AnimatedButtonWrapper = styled("div")(({ theme }) => ({
	display: "flex",
	transition: "all 0.2s ease-in-out",
	transform: "scale(1)",
	"&:hover": {
		transform: "scale(1.05)",
	},
}));
const ImgTag = styled("img")({
	width: "1.688rem",
	height: "1.688rem",
	objectFit: "contain",
	borderRadius: "0.5rem",
	cursor: "pointer",
	transition: "transform 0.2s ease-in-out",
	"&:hover": {
		transform: "scale(1.05)",
	},
});
const InputContent = styled("input")(({ theme }) => ({
	display: "none",
}));

const ContentContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "center",
	width: "47%",
	height: "10.625rem",
	borderRadius: "16px",
	borderColor: theme.palette.primary.main,
}));

const ImageContainer = styled("div")(({ theme }) => ({
	position: "relative",
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
}));

const PreviewImage = styled("img")(({ theme }) => ({
	width: "100%",
	height: "10.238rem",
	borderRadius: "16px ",
}));

const Snack = styled(Snackbar)(({ theme }) => ({
	// [theme.breakpoints.down("md")]: {
	// 	bottom: "5rem",
	// 	borderRadius: "0.5rem",
	// },
}));

const FormControlContainer = styled(FormControl)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	padding: "var(--Spacing-spacing-sm, 0.5rem)",
	gap: "var(--Spacing-spacing-sm, 0.5rem)",
	alignSelf: "stretch",
	width: "100%",
	height: "3rem",
	position: "absolute",
	bottom: "0.3rem",
}));

const ProgressContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "center",
	alignItems: "center",
	position: "absolute",
	width: "100%",
	height: "100%",
	zIndex: 0,
}));

const ImagePreviewContainer = styled(RadioGroup)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "100%",
	maxHeight: "30rem",
	gap: "1rem",
	flexWrap: "wrap",
}));

const PreviewGrid = styled("div")(({ theme }) => ({
	display: "grid",
	width: "100%",
	padding: "1rem 0",
	gap: "1rem",
	maxHeight: "420px",
	overflowY: "auto",
	overflowX: "hidden",
	[theme.breakpoints.down("sm")]: {
		gridTemplateColumns: "repeat(2, 1fr)",
	},
	[theme.breakpoints.between("sm", "md")]: {
		gridTemplateColumns: "repeat(2, 1fr)",
	},
	[theme.breakpoints.up("md")]: {
		gridTemplateColumns: "repeat(2, minmax(150px, 1fr))",
		maxWidth: "600px",
	},
}));
const ImageCard = styled("div")(({ theme }) => ({
	position: "relative",
	aspectRatio: "1",
	borderRadius: "1rem",
	overflow: "hidden",
	boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
	transition: "transform 0.2s ease-in-out",
	height: "150px", // Set fixed height
	"&:hover": {
		transform: "scale(1.02)",
	},
}));

const StyledPreviewImage = styled("img")({
	width: "100%",
	height: "100%",
	objectFit: "cover",
});

const PreviewDrawer = styled(Drawer)(({ theme }) => ({
	"& .MuiDrawer-paper": {
		width: "100%",
		maxHeight: "40vh",
		borderTopLeftRadius: "1rem",
		borderTopRightRadius: "1rem",
		padding: "1rem",
		background: theme.palette.background.paper,
	},
}));

const DrawerHeader = styled("div")({
	display: "flex",
	justifyContent: "space-between",
	alignItems: "center",
	padding: "0 1rem 1rem 1rem",
	borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
});

interface UploadProgress {
	fileId: ObjectId | null;
	percentage: number;
	stage: string;
}

interface FileAndMore {
	file: File;
	mime_type: string;
}

const ChatInput = ({
	session,
	dispatch,
	router,
	profile_context,
	initial_load_done,
	messages_loading,
	chat_id,
	selectedFilePreview,
	pendingUploads,
	setPendingUploads,
	setSelectedFilePreview,
	current_message,
	setCurrentMessage,
	setSendFiles,
	sendFiles,
	messages_count,
}: {
	session: StateInterface["redux_session"];
	dispatch: Function;
	router: NextRouter;
	profile_context: any;
	initial_load_done: boolean;
	messages_loading: boolean;
	chat_id: ObjectId | string;
	selectedFilePreview: Array<string>;
	pendingUploads: Array<FileAndMore>;
	setPendingUploads: React.Dispatch<React.SetStateAction<Array<FileAndMore>>>;
	setSelectedFilePreview: React.Dispatch<React.SetStateAction<Array<string>>>;
	current_message: string;
	setCurrentMessage: React.Dispatch<React.SetStateAction<string>>;
	setSendFiles: React.Dispatch<React.SetStateAction<boolean>>;
	sendFiles: boolean;
	messages_count: number;
}) => {

	const theme = useTheme();

	/* Translations */

	const localLang = {
		message: router.locale === "ar-AE" ? "رسالة" : router.locale === "kn-IN" ? "ಸಂದೇಶ" : "Message",
		start_typing:
			router.locale === "ar-AE"
				? "... بدء الكتابة"
				: router.locale === "kn-IN"
					? "ಟೈಪಿಂಗ್ ಪ್ರಾರಂಭಿಸಿ ..."
					: "Start typing ...",
	};

	const input = React.useRef<HTMLInputElement>(null);

	const [message, setMessage] = React.useState<string>("");

	const [stage, setStage] = React.useState<Array<string>>(["NOT_STARTED"]);

	const [fileId, setFileId] = React.useState<Array<ObjectId | null>>([null]);

	const [fileErrorSnackbarText, setFileErrorSnackbarText] = React.useState<string>("File size or type not supported");

	const [openSnackbar, setOpenSnackbar] = React.useState(false);

	const [sendButtonDisabled, setSendButtonDisabled] = React.useState<boolean>(false);

	const chipsValue = [
		{
			value: "What’s the commission structure?",
		},
		{
			value: "Can we get on a quick call?",
		},
		{
			value: "I have a client for your property, can we talk?",
		},
		{
			value: "Hi, are you open to channel partner tie-ups?",
		},
	];

	/* Handle Textfield Change */

	const handleTextFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setMessage(event.target.value);
		if (pendingUploads.length > 0) {
			setCurrentMessage(event.target.value);
		}
	};

	/* Send Message Function */

	const handleSendMessage = async () => {
		if (pendingUploads.length > 0) {
			setSendFiles(true);

			setSendButtonDisabled(true);
		} else {
			dispatch(
				sendChatMessageThunk({
					chat_id: chat_id,
					sender_is_business_profile: profile_context.is_business_profile,
					sender_user_id: session ? (session.user ? session.user.dbUser._id : "") : "",
					sender_business_profile_id: profile_context.business_profile_id,
					message_body: message,
					attachments: [],
					sessionId: session ? (session.session_id ? session.session_id : "") : "",
				}),
			);
		}

		setMessage("");
	};

	const handleRemoveImage = async (index: number) => {
		const newSelectedFilePreview = [...selectedFilePreview];
		const newPendingUploads = [...pendingUploads];
		const newFileId = [...fileId];
		const newStage = [...stage];

		// Revoke the blob URL to free up memory
		URL.revokeObjectURL(selectedFilePreview[index]);

		newSelectedFilePreview.splice(index, 1);
		newPendingUploads.splice(index, 1);
		newFileId.splice(index, 1);
		newStage.splice(index, 1);

		setSelectedFilePreview(newSelectedFilePreview);
		setPendingUploads(newPendingUploads);
		setFileId(newFileId);
		setStage(newStage);
	};

	React.useEffect(() => {
		if (pendingUploads.length > 0) {
			setSendButtonDisabled(false);
		}
	}, [pendingUploads]);

	return (
		<RootDiv>
			{/* Message Input Field */}
			<InputContent
				ref={input}
				type="file"
				accept="image/jpeg, image/png"
				multiple
				onChange={async (event: React.ChangeEvent<HTMLInputElement>) => {
					const files = event.target.files;
					const allowedTypes = ["image/jpeg", "image/png"];
					const allowedExtensions = ["jpg", "jpeg", "png"];
					const uploadType: string = "property_or_services";

					if (files && files.length > 0) {
						const {
							validFiles,
							invalidFiles,
						}: {
							validFiles: Array<FileAndMore>;
							invalidFiles: Array<File>;
						} = await validateFiles(Array.from(files), allowedTypes, allowedExtensions, uploadType);

						if (validFiles.length > 0) {
							// Create blob URLs for preview
							const previews = validFiles.map((f) => URL.createObjectURL(f.file));
							setSelectedFilePreview((prev) => [...prev, ...previews]);
							setPendingUploads((prev) => [...prev, ...validFiles]);
						}

						if (invalidFiles.length > 0) {
							const fileNames: Array<string> = invalidFiles.map((file: File) => file.name);
							setFileErrorSnackbarText(
								(fileNames.join(", ").length > 15 ? fileNames.join(", ").slice(0, 15) + "..." : fileNames.join(", ")) +
									(invalidFiles.length === 1 ? " is an" : " are") +
									" unsupported " +
									(invalidFiles.length === 1 ? "file" : "files") +
									" and " +
									(invalidFiles.length === 1 ? "has" : "have") +
									" been deselected automatically.",
							);
							setOpenSnackbar(true);
						} else {
							setOpenSnackbar(false);
						}
					} else {
						setOpenSnackbar(true);
					}
				}}
			/>
			{selectedFilePreview.length === 0 && messages_count === 1 ? (
				<div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
					{chipsValue.map((chip, index) => (
						<BeegruButton
							key={index}
							flavor="primary"
							variant="contained"
							disableRipple
							disableTouchRipple
							disableFocusRipple
							disableElevation
							onClick={() => setMessage(chip.value)}
							sx={{
								borderRadius: "1rem",
								textTransform: "none",
								fontSize: "0.875rem",
								padding: "0.5rem 1rem",
								border: "1px solid #e0e0e0",
								"&.MuiButton-contained": {
									background: "#FFFFFF",
									color: theme.palette.primary.main,
									boxShadow: "none",
									"&:hover": {
										background: "#FFFFFF",
										boxShadow: "none",
									},
									"&:active": {
										background: "#FFFFFF",
										boxShadow: "none",
									},
									"&:focus": {
										background: "#FFFFFF",
										boxShadow: "none",
									},
								},
							}}
						>
							{chip.value}
						</BeegruButton>
					))}
				</div>
			) : null}
			<div style={{ display: "flex", flexDirection: "row", width: "100%", gap: "0.5rem" }}>
				<ChatContainer
					sx={{
						display: initial_load_done ? "flex" : "none",
						flexDirection: "column",
					}}
				>
					{selectedFilePreview.length > 0 && !sendFiles && (
						<PreviewGrid>
							{selectedFilePreview.map((file: string, index: number) => (
								<ImageCard key={index}>
									{file !== "" ? (
										<>
											<StyledPreviewImage
												alt="Property Images"
												src={file}
												referrerPolicy="no-referrer"
											/>
											<ProgressContainer>
												{(stage[index] === undefined ||
													stage[index] === "NOT_STARTED" ||
													(stage[index] && stage[index] !== "FETCHED_FILE_INFORMATION")) && (
													<CircularProgress
														size={24}
														sx={{
															color: "#FFF",
															position: "absolute",
															top: "50%",
															left: "50%",
															transform: "translate(-50%, -50%)",
															zIndex: 1,
														}}
													/>
												)}
											</ProgressContainer>
											<IconButton
												size="small"
												onClick={() => handleRemoveImage(index)}
												sx={{
													position: "absolute",
													top: "0.5rem",
													right: "0.5rem",
													background: "rgba(255, 255, 255, 0.9)",
													backdropFilter: "blur(4px)",
													"&:hover": {
														background: "rgba(255, 255, 255, 1)",
													},
													width: "2rem",
													height: "2rem",
													padding: "0.25rem",
												}}
											>
												<DeleteOutline sx={{ color: "#E31B0C", fontSize: "1.25rem" }} />
											</IconButton>
										</>
									) : (
										<CircularProgress />
									)}
								</ImageCard>
							))}
						</PreviewGrid>
					)}
					<div
						style={{
							display: "flex",
							flexDirection: "row",
							width: "100%",
							position: "relative",
							gap: "0.5rem",
						}}
					>
						<TextField
							value={message}
							onChange={handleTextFieldChange}
							variant="standard"
							size="small"
							fullWidth
							placeholder={localLang.start_typing}
							slotProps={{
								input: {
									disableUnderline: true,
									autoComplete: "off",
								},
							}}
							onKeyUp={(e) => {
								if (e.key === "Enter" && message.length > 0) {
									handleSendMessage();
								}
							}}
						/>

						<IconButton
							onClick={() => input.current?.click()}
							sx={{ height: "1.688rem", width: "1.688rem" }}
						>
							<AddPhotoAlternateOutlinedIcon
								sx={{
									fontSize: "1.2rem",
									color: "#6c757d",
									cursor: "pointer",
								}}
							/>
						</IconButton>
						<IconButton sx={{ height: "1.688rem", width: "1.688rem" }}>
							<AttachFileOutlinedIcon
								sx={{
									fontSize: "1.2rem",
									color: "#6c757d",
									cursor: "pointer",
								}}
							/>
						</IconButton>
						<Fade in={selectedFilePreview.length > 0 && !sendButtonDisabled}>
							<AnimatedButtonWrapper
								sx={{
									display: selectedFilePreview.length === 0 || sendButtonDisabled ? "none" : "flex",
								}}
							>
								<BeegruButton
									flavor="success"
									variant="contained"
									onClick={handleSendMessage}
									disabled={selectedFilePreview.length === 0 || messages_loading}
									sx={{
										borderRadius: "1rem",
										transition: "all 0.2s ease-in-out",
									}}
								>
									<SendOutlined
										sx={{
											fontSize: "1rem",
										}}
									/>
								</BeegruButton>
							</AnimatedButtonWrapper>
						</Fade>
					</div>
				</ChatContainer>

				{/* Send Button */}

				<Fade in={!messages_loading && message.length > 0 && selectedFilePreview.length === 0}>
					<AnimatedButtonWrapper
						sx={{
							display: messages_loading || message.length === 0 || selectedFilePreview.length !== 0 ? "none" : "flex",
						}}
					>
						<BeegruButton
							flavor="success"
							variant="contained"
							onClick={handleSendMessage}
							disabled={messages_loading || message.length === 0}
							sx={{
								borderRadius: "1rem",
								transition: "all 0.2s ease-in-out",
							}}
						>
							<SendOutlined
								sx={{
									fontSize: "1.5rem",
								}}
							/>
						</BeegruButton>
					</AnimatedButtonWrapper>
				</Fade>
			</div>
		</RootDiv>
	);
};

export default ChatInput;
